import { useEffect, useState } from 'react';
import { apiRequests } from '../../shared/api/apiRequests';
import { useParams, useNavigate } from 'react-router-dom';
import { CONTENT_ITEM_KEYS } from '../../features/CreateContent/constants';
import { toast } from 'react-toastify';
import { Form, Row, Col } from 'react-bootstrap';
import { CreateContent } from '../../features/CreateContent/index.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Availability } from '../../entities/Availability/index.js';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { mapContentAudio, mapContentItems } from '../../shared/utils/mapContent.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

export const EditPortfolioPage = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Загружаем все данные параллельно
        const [portfolioRes, categoriesRes, industriesRes] = await Promise.all([
          apiRequests.portfolio.getById(id),
          apiRequests.portfolio.getCategories(),
          apiRequests.portfolio.getIndustries(),
        ]);

        setCategories(categoriesRes.items || []);
        setIndustries(industriesRes.items || []);

        // Маппим SEO метатеги с правильным регистром
        const seoData = portfolioRes.seoMetatags || portfolioRes.SEOMetatags || {};

        const formData = {
          title: portfolioRes.title || '',
          shortDescription: portfolioRes.shortDescription || '',
          categoryId: portfolioRes.categoryId,
          industryId: portfolioRes.industryId,
          slug: portfolioRes.slug || '',
          mainLanguageId: portfolioRes.mainLanguageId,
          content: {
            items: portfolioRes.content?.items?.map((i) => ({
              guid: i.guid,
              discriminator: i.discriminator,
              [CONTENT_ITEM_KEYS[i.discriminator]]: i[CONTENT_ITEM_KEYS[i.discriminator]],
            })) || [],
          },
          imgPath: portfolioRes.imgPath || '',
          availability: {
            availableInAllCountries: portfolioRes.availability?.availableInAllCountries ?? true,
            allowedCountriesIds: portfolioRes.availability?.allowedCountriesIds || [],
            disallowedCountriesIds: portfolioRes.availability?.disallowedCountriesIds || [],
          },
          SEOMetatags: {
            Title: seoData.title || seoData.Title || '',
            Description: seoData.description || seoData.Description || '',
            Keywords: seoData.keywords || seoData.Keywords || '',
            title: seoData.title || seoData.Title || '',
            description: seoData.description || seoData.Description || '',
            keywords: seoData.keywords || seoData.Keywords || '',
          },
        };

        setForm(formData);
        setData(portfolioRes);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        toast.error('Ошибка загрузки данных портфолио');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('Введите название портфолио');
      return;
    }

    setSaving(true);
    const body = new FormData();

    try {
      // Обрабатываем контент
      const bodyItems = form.content.items.map((i) => {
        switch (i.discriminator) {
          case 'TextContentItem':
            return i;
          case 'ImageSliderContentItem':
            const { content, model } = mapContentItems(i, true, data.content.items);
            for (const pair of content) {
              body.append(pair[0], pair[1]);
            }
            return { ...i, images: model };
          default:
            const { isUpdateAudio } = mapContentAudio(i, data.content.items);
            if (isUpdateAudio) body.append(i.guid, i[CONTENT_ITEM_KEYS[i.discriminator]]);
            return { ...i, [CONTENT_ITEM_KEYS[i.discriminator]]: '' };
        }
      });

      const bodyModel = JSON.parse(JSON.stringify(form));
      bodyModel.content.items = bodyItems;

      if (mainImage) {
        body.append('mainImg', mainImage);
      }

      // Обновление доступности
      if (hasChangesAvailability(data.availability, bodyModel.availability)) {
        await apiRequests.portfolio.updateAvailability(bodyModel.availability);
      }

      body.append('model', JSON.stringify({ ...bodyModel, id: data.id }));

      await apiRequests.portfolio.update(body);
      toast.success('Портфолио успешно обновлено');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Произошла ошибка при сохранении';
      toast.error(errorMessage);
      console.error('Ошибка:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContent = (value) => {
    setForm((prev) => ({
      ...prev,
      content: { items: [...prev.content.items, { guid: value.id, discriminator: value.type }] },
    }));
  };

  const handleChangeContent = (value) => {
    setForm((prev) => {
      const newContentItems = prev.content.items.map((i) => {
        if (i.guid === value.id) {
          if (value.type === 'TextContentItem') {
            return { ...i, guid: value.id, [CONTENT_ITEM_KEYS[value.type]]: value.content };
          } else {
            return { ...i, guid: value.newGuid, [CONTENT_ITEM_KEYS[value.type]]: value.content };
          }
        }
        return i;
      });
      return { ...prev, content: { items: newContentItems } };
    });
  };

  const handleDeleteContent = (contentId) => {
    setForm((prev) => ({
      ...prev,
      content: { items: prev.content.items.filter((i) => i.guid !== contentId) },
    }));
  };

  if (loading) return <FormLoading />;
  if (!form) return null;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/portfolio')}
      saving={saving}
      submitText="Сохранить изменения"
    >
      {/* Основная информация */}
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название портфолио" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название"
              />
            </FormField>

            <FormField label="Краткое описание">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.shortDescription}
                onChange={(e) => setForm((prev) => ({ ...prev, shortDescription: e.target.value }))}
                placeholder="Введите краткое описание проекта"
              />
            </FormField>

            <Row>
              <Col md={6}>
                <FormField label="Категория">
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Индустрия">
                  <Form.Select
                    value={form.industryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, industryId: Number(e.target.value) }))}
                  >
                    {industries.map((ind) => (
                      <option key={ind.id} value={ind.id}>{ind.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField label="Изображение">
              <UploadPicture 
                onSelect={setMainImage} 
                initialValue={form.imgPath} 
                entityType="portfolio" 
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      {/* Контент */}
      <FormSection title="Контент страницы">
        {form.content?.items && (
          <CreateContent
            initialValues={form.content.items}
            onDelete={handleDeleteContent}
            onChange={handleChangeContent}
            onAdd={handleAddContent}
          />
        )}
      </FormSection>

      {/* Настройки */}
      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              initialValue={form.mainLanguageId}
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Form.Label className="fw-medium mb-3">Доступность по странам</Form.Label>
          <Availability
            initialValues={form.availability}
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      {/* SEO */}
      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.SEOMetatags}
          onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
        />
      </FormSection>
    </FormLayout>
  );
};
